import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

import { BehaviorSubject, catchError, combineLatest, map, Observable, of, Subscription, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { IconPosition } from '@fcom/ui-components/components/icons';
import { ButtonPosition, ButtonTheme } from '@fcom/ui-components/components/buttons';
import { FinnairBoundItem, FinnairOrder } from '@fcom/dapi/api/models';
import { finShare } from '@fcom/rx/operators/finShare';
import { CommonBookingFeatureState, OrderActions } from '@fcom/common-booking/store';
import { MmbOrderService } from '@fcom/manage-booking-common/services';
import { OrderStatus } from '@fcom/common-booking';
import { RadioButtonTheme } from '@fcom/ui-components/components/forms';
import { CheckInService } from '@fcom/manage-booking-common/services/check-in.service';

import { ManageBookingFeatureState } from '../../../interfaces';

interface VoluntaryDeniedBoardingAcceptanceOrderResponse {
  order: FinnairOrder;
  confirmation: {
    isOptedIn: boolean;
    reason?: string;
  };
}

@Component({
  selector: 'fin-check-in-voluntary-denied-boarding-route',
  templateUrl: './check-in-voluntary-denied-boarding-route-component.html',
  styleUrls: ['./check-in-voluntary-denied-boarding-route-component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInVoluntaryDeniedBoardingComponent implements OnInit {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly OrderStatus = OrderStatus;
  readonly RadioButtonTheme = RadioButtonTheme;
  readonly IconPosition = IconPosition;

  private mmbOrderService = inject(MmbOrderService);
  private checkInService = inject(CheckInService);
  private fb = inject(FormBuilder);

  private store$ = inject<Store<CommonBookingFeatureState & ManageBookingFeatureState>>(Store);
  order$: Observable<FinnairOrder>;
  bound$: Observable<FinnairBoundItem>;
  journeyId$: Observable<string>;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  orderStatus$: Observable<OrderStatus>;
  departure$: Observable<string>;
  arrival$: Observable<string>;
  compensation$: Observable<{ giftCard: string; avios: string; monetary: string } | undefined>;
  confirmForm: FormGroup;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.order$ = this.checkInService.getOrder();
    this.bound$ = this.checkInService.getBound();
    this.orderStatus$ = this.checkInService.getOrderStatus();
    this.confirmForm = this.fb.group({
      confirmChoice: [null as 'true' | 'false' | null, Validators.required],
    });

    this.journeyId$ = combineLatest([this.order$, this.bound$]).pipe(
      map(([order, bound]) => {
        return order?.checkIn?.dictionary?.journey?.[bound.id];
      }),
      finShare()
    );

    this.order$.pipe(take(1)).subscribe((order) => {
      const locations = order.locations;

      this.departure$ = combineLatest([this.bound$]).pipe(
        map(([bound]) => locations[bound.departure.locationCode]?.cityName),
        take(1),
        finShare()
      );

      this.arrival$ = combineLatest([this.bound$]).pipe(
        map(([bound]) => locations[bound.arrival.locationCode]?.cityName),
        take(1),
        finShare()
      );
    });

    this.compensation$ = combineLatest([this.order$, this.bound$]).pipe(
      map(([order, bound]) => {
        const eligibility =
          order.eligibilities?.checkIn?.find((e) => e.id === bound.id)?.flightTravelerEligibility ?? {};

        const allEligibilities = Object.values(eligibility).flat();
        const validVoluntaryDeniedBoardingEligibilities = allEligibilities.filter(
          (e) => e.voluntaryDeniedBoarding?.isAllowedToUseVoluntaryDeniedBoarding
        );

        if (!validVoluntaryDeniedBoardingEligibilities.length) {
          return undefined;
        }

        const compensation = validVoluntaryDeniedBoardingEligibilities[0].voluntaryDeniedBoarding.compensation;

        return {
          giftCard: compensation?.giftCard?.appliedValue,
          avios: compensation?.avios?.amount,
          monetary: compensation?.monetary?.value.toString() ?? '',
        };
      }),
      take(1),
      finShare()
    );
  }

  confirmVoluntaryDeniedBoarding(): void {
    if (this.confirmForm.invalid) {
      return;
    }
    const choice = this.confirmForm.controls.confirmChoice.value;
    if (choice === 'false') {
      this.checkInService.triggerNextCheckInStep();
      return;
    }

    if (choice === 'true') {
      this.loading$.next(true);
      this.subscriptions.add(
        combineLatest([this.order$, this.bound$, this.journeyId$])
          .pipe(
            take(1),
            switchMap(([order, _, journeyId]) =>
              this.mmbOrderService
                .fetchOrderAfterRequest(
                  this.checkInService.confirmVoluntaryDeniedBoarding$(order.id, order.hash, journeyId)
                )
                .pipe(
                  map(
                    (orderData) =>
                      ({
                        order: orderData,
                        confirmation: { isOptedIn: true },
                      }) as VoluntaryDeniedBoardingAcceptanceOrderResponse
                  ),
                  catchError(() =>
                    of({
                      order,
                      confirmation: { isOptedIn: false },
                    })
                  )
                )
            ),
            finShare()
          )
          .subscribe({
            next: ({ order }) => {
              this.store$.dispatch(OrderActions.setOrderData({ order }));
              this.checkInService.triggerNextCheckInStep();
              this.loading$.next(false);
            },
            error: () => {
              this.checkInService.triggerNextCheckInStep();
              this.loading$.next(false);
            },
          })
      );
    }
  }
}
